import { defineStore } from 'pinia';
import { collection, deleteField, doc, runTransaction, where } from 'firebase/firestore';
import { resolveManualDurationSeconds } from '~/lib/timeTracking';
import { toMillis } from '~/lib/timestamps';
import { stripUndefined } from '~/lib/stripUndefined';
import { runFreshRequest, type FreshRequestOptions } from '~/lib/requestFreshness';
import {
    createTenantDocument,
    deleteTenantDocument,
    fetchTenantCollection,
    updateTenantDocument,
    type TenantDocumentPayload,
} from '~/lib/tenantCollection';
import type { TimeEntry } from '~/types/models';

type TimeEntryContext = {
    taskId?: string;
    taskTitle?: string;
    projectId?: string;
    projectName?: string;
    clientId?: string;
    clientName?: string;
    description?: string;
    billable?: boolean;
};

type ManualTimeEntryInput = TimeEntryContext & {
    startedAt: Date;
    endedAt: Date;
    userId?: string;
    userName?: string;
};

const ACTIVE_TIMER_MAX_AGE_MS = 15_000;

export const useTimeEntriesStore = defineStore('time-entries', {
    state: () => ({
        entries: [] as TimeEntry[],
        loading: false,
        saving: false,
        error: null as string | null,
        loadedTenantId: null as string | null,
        loadedRangeKeys: [] as string[],
        loadedRangeEntryIds: [] as string[],
        loadedProjectIds: [] as string[],
    }),

    getters: {
        runningEntry: (state): TimeEntry | null => {
            const authStore = useAuthStore();
            const userId = authStore.user?.uid || '';
            return state.entries.find(entry => ['running', 'paused'].includes(entry.status) && entry.userId === userId) || null;
        },
    },

    actions: {
        _tenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) throw new Error('No tenant selected. Please log in.');
            return tenantStore.currentTenantId;
        },

        _currentUser(): { id: string; name: string } {
            const authStore = useAuthStore();
            const userStore = useUserStore();
            const id = authStore.user?.uid || userStore.user?.id || '';
            if (!id) throw new Error('You must be signed in to track time.');
            return {
                id,
                name: authStore.user?.displayName || userStore.user?.displayName || userStore.user?.fullName || authStore.user?.email || 'Team member',
            };
        },

        _sort(entries: TimeEntry[]): TimeEntry[] {
            return [...entries].sort((a, b) => toMillis(b.startedAt) - toMillis(a.startedAt));
        },

        _merge(entries: TimeEntry[]): void {
            const byId = new Map(this.entries.map(entry => [entry.id, entry]));
            for (const entry of entries) byId.set(entry.id, entry);
            this.entries = this._sort(Array.from(byId.values()));
        },

        async fetchRange(startedAfter: Date, startedBefore: Date, options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            this.loading = true;
            this.error = null;
            if (this.loadedTenantId !== tenantId) {
                this.entries = [];
                this.loadedRangeKeys = [];
                this.loadedRangeEntryIds = [];
                this.loadedProjectIds = [];
            }

            try {
                const rangeKey = `${startedAfter.getTime()}:${startedBefore.getTime()}`;
                await Promise.all([
                    runFreshRequest(`time-entries:${tenantId}:range:${rangeKey}`, async () => {
                        const rangeEntries = await fetchTenantCollection<TimeEntry>($db, tenantId, 'timeEntries', [
                            where('startedAt', '>=', startedAfter),
                            where('startedAt', '<=', startedBefore),
                        ]);
                        const previousRangeIds = new Set(this.loadedRangeEntryIds);
                        this.entries = this.entries.filter(entry => (
                            !previousRangeIds.has(entry.id)
                            || ['running', 'paused'].includes(entry.status)
                            || Boolean(entry.projectId && this.loadedProjectIds.includes(entry.projectId))
                        ));
                        this._merge(rangeEntries);
                        this.loadedRangeEntryIds = rangeEntries.map(entry => entry.id);
                        this.loadedRangeKeys = [rangeKey];
                        this.loadedTenantId = tenantId;
                    }, {
                        ...options,
                        hasData: this.loadedTenantId === tenantId && this.loadedRangeKeys.includes(rangeKey),
                    }),
                    this.fetchRunning(options),
                ]);
            } catch (error: any) {
                this.error = error?.message || 'Unable to load time entries.';
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async fetchRunning(options: FreshRequestOptions = {}): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            try {
                if (this.loadedTenantId !== tenantId) {
                    this.entries = [];
                    this.loadedRangeKeys = [];
                    this.loadedRangeEntryIds = [];
                    this.loadedProjectIds = [];
                }
                await runFreshRequest(`time-entries:${tenantId}:running`, async () => {
                    const entries = await fetchTenantCollection<TimeEntry>($db, tenantId, 'timeEntries', [where('status', 'in', ['running', 'paused'])]);
                    this.entries = this.entries.filter(entry => !['running', 'paused'].includes(entry.status));
                    this._merge(entries);
                    this.loadedTenantId = tenantId;
                }, {
                    maxAgeMs: ACTIVE_TIMER_MAX_AGE_MS,
                    ...options,
                    hasData: this.loadedTenantId === tenantId,
                });
            } catch (error: any) {
                this.error = error?.message || 'Unable to load the active timer.';
                throw error;
            }
        },

        async fetchForProject(projectId: string, options: FreshRequestOptions = {}): Promise<void> {
            if (!projectId) return;
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            try {
                if (this.loadedTenantId !== tenantId) {
                    this.entries = [];
                    this.loadedRangeKeys = [];
                    this.loadedRangeEntryIds = [];
                    this.loadedProjectIds = [];
                }
                await runFreshRequest(`time-entries:${tenantId}:project:${projectId}`, async () => {
                    const entries = await fetchTenantCollection<TimeEntry>($db, tenantId, 'timeEntries', [where('projectId', '==', projectId)]);
                    this._merge(entries);
                    if (!this.loadedProjectIds.includes(projectId)) this.loadedProjectIds = [...this.loadedProjectIds, projectId];
                    this.loadedTenantId = tenantId;
                }, {
                    ...options,
                    hasData: this.loadedTenantId === tenantId && this.loadedProjectIds.includes(projectId),
                });
            } catch (error: any) {
                this.error = error?.message || 'Unable to load tracked project time.';
                throw error;
            }
        },

        async startTimer(context: TimeEntryContext = {}): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            const user = this._currentUser();
            this.saving = true;
            this.error = null;

            try {
                await this.fetchRunning({ force: true });
                if (this.runningEntry) throw new Error('Stop your current timer before starting another one.');
                const now = new Date();
                const payload = stripUndefined({
                    tenantId,
                    userId: user.id,
                    userName: user.name,
                    ...context,
                    billable: context.billable === true,
                    status: 'running' as const,
                    startedAt: now,
                    activeStartedAt: now,
                    durationSeconds: 0,
                    createdAt: now,
                    updatedAt: now,
                });
                const entryRef = doc(collection($db, `tenants/${tenantId}/timeEntries`));
                const stateRef = doc($db, `tenants/${tenantId}/timeTrackingStates/${user.id}`);
                await runTransaction($db, async (transaction) => {
                    const stateSnapshot = await transaction.get(stateRef);
                    if (stateSnapshot.exists()) {
                        const activeEntryId = String(stateSnapshot.data().entryId || '');
                        if (activeEntryId) {
                            const activeEntrySnapshot = await transaction.get(doc($db, `tenants/${tenantId}/timeEntries/${activeEntryId}`));
                            if (activeEntrySnapshot.exists() && ['running', 'paused'].includes(activeEntrySnapshot.data().status)) {
                                throw new Error('Stop your current timer before starting another one.');
                            }
                        }
                    }
                    transaction.set(entryRef, payload);
                    transaction.set(stateRef, { tenantId, userId: user.id, entryId: entryRef.id, status: 'running', startedAt: now });
                });
                const id = entryRef.id;
                this._merge([{ id, ...payload } as TimeEntry]);
                return id;
            } catch (error: any) {
                this.error = error?.message || 'Unable to start the timer.';
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async pauseTimer(entryId?: string): Promise<void> {
            const entry = this.entries.find(item => item.id === (entryId || this.runningEntry?.id));
            if (!entry || entry.status !== 'running') throw new Error('No running timer was found.');
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            const pausedAt = new Date();
            const activeStartedAtMs = toMillis(entry.activeStartedAt || entry.startedAt);
            const durationSeconds = Math.max(1, Number(entry.durationSeconds || 0) + Math.floor((pausedAt.getTime() - activeStartedAtMs) / 1000));
            const entryRef = doc($db, `tenants/${tenantId}/timeEntries/${entry.id}`);
            const stateRef = doc($db, `tenants/${tenantId}/timeTrackingStates/${entry.userId}`);
            this.saving = true;
            this.error = null;
            try {
                await runTransaction($db, async (transaction) => {
                    const state = await transaction.get(stateRef);
                    const current = await transaction.get(entryRef);
                    if (!state.exists() || state.data().entryId !== entry.id || !current.exists()
                        || current.data().status !== 'running'
                        || toMillis(current.data().updatedAt) !== toMillis(entry.updatedAt)) {
                        throw new Error('This timer changed on another device. Refresh before pausing it.');
                    }
                    transaction.update(entryRef, { status: 'paused', durationSeconds, activeStartedAt: deleteField(), updatedAt: pausedAt });
                    transaction.set(stateRef, { tenantId, userId: entry.userId, entryId: entry.id, status: 'paused', startedAt: entry.startedAt }, { merge: true });
                });
                this.entries = this._sort(this.entries.map(item => item.id === entry.id
                    ? { ...item, status: 'paused', durationSeconds, activeStartedAt: undefined, updatedAt: pausedAt }
                    : item));
            } catch (error: any) {
                this.error = error?.message || 'Unable to pause the timer.';
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async resumeTimer(entryId?: string): Promise<void> {
            const entry = this.entries.find(item => item.id === (entryId || this.runningEntry?.id));
            if (!entry || entry.status !== 'paused') throw new Error('No paused timer was found.');
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            const resumedAt = new Date();
            const entryRef = doc($db, `tenants/${tenantId}/timeEntries/${entry.id}`);
            const stateRef = doc($db, `tenants/${tenantId}/timeTrackingStates/${entry.userId}`);
            this.saving = true;
            this.error = null;
            try {
                await runTransaction($db, async (transaction) => {
                    const state = await transaction.get(stateRef);
                    const current = await transaction.get(entryRef);
                    if (!state.exists() || state.data().entryId !== entry.id || !current.exists()
                        || current.data().status !== 'paused'
                        || toMillis(current.data().updatedAt) !== toMillis(entry.updatedAt)) {
                        throw new Error('This timer changed on another device. Refresh before resuming it.');
                    }
                    transaction.update(entryRef, { status: 'running', activeStartedAt: resumedAt, endedAt: deleteField(), updatedAt: resumedAt });
                    transaction.set(stateRef, { tenantId, userId: entry.userId, entryId: entry.id, status: 'running', startedAt: entry.startedAt }, { merge: true });
                });
                this.entries = this._sort(this.entries.map(item => item.id === entry.id
                    ? { ...item, status: 'running', activeStartedAt: resumedAt, endedAt: undefined, updatedAt: resumedAt }
                    : item));
            } catch (error: any) {
                this.error = error?.message || 'Unable to resume the timer.';
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async stopTimer(entryId?: string): Promise<void> {
            const entry = this.entries.find(item => item.id === (entryId || this.runningEntry?.id));
            if (!entry || !['running', 'paused'].includes(entry.status)) throw new Error('No active timer was found.');
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            const endedAt = new Date();
            const activeStartedAtMs = toMillis(entry.activeStartedAt || entry.startedAt);
            const durationSeconds = entry.status === 'paused'
                ? Math.max(1, Number(entry.durationSeconds || 0))
                : Math.max(1, Number(entry.durationSeconds || 0) + Math.floor((endedAt.getTime() - activeStartedAtMs) / 1000));
            const entryRef = doc($db, `tenants/${tenantId}/timeEntries/${entry.id}`);
            const stateRef = doc($db, `tenants/${tenantId}/timeTrackingStates/${entry.userId}`);
            this.saving = true;
            this.error = null;
            try {
                await runTransaction($db, async (transaction) => {
                    const stateSnapshot = await transaction.get(stateRef);
                    const current = await transaction.get(entryRef);
                    if (!current.exists() || current.data().status !== entry.status
                        || toMillis(current.data().updatedAt) !== toMillis(entry.updatedAt)) {
                        throw new Error('This timer changed on another device. Refresh before stopping it.');
                    }
                    transaction.update(entryRef, { status: 'stopped', endedAt, durationSeconds, activeStartedAt: deleteField(), updatedAt: endedAt });
                    if (stateSnapshot.exists() && stateSnapshot.data().entryId === entry.id) transaction.delete(stateRef);
                });
                this.entries = this._sort(this.entries.map(item => item.id === entry.id
                    ? { ...item, status: 'stopped', endedAt, durationSeconds, activeStartedAt: undefined, updatedAt: endedAt }
                    : item));
            } catch (error: any) {
                this.error = error?.message || 'Unable to stop the timer.';
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async addManualEntry(input: ManualTimeEntryInput): Promise<string> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            const currentUser = this._currentUser();
            const now = new Date();
            const payload = stripUndefined({
                tenantId,
                userId: input.userId || currentUser.id,
                userName: input.userName || currentUser.name,
                taskId: input.taskId,
                taskTitle: input.taskTitle,
                projectId: input.projectId,
                projectName: input.projectName,
                clientId: input.clientId,
                clientName: input.clientName,
                description: input.description,
                billable: input.billable === true,
                status: 'stopped' as const,
                startedAt: input.startedAt,
                endedAt: input.endedAt,
                durationSeconds: resolveManualDurationSeconds(input.startedAt, input.endedAt),
                createdAt: now,
                updatedAt: now,
            });
            const id = await createTenantDocument($db, tenantId, 'timeEntries', payload as TenantDocumentPayload);
            this._merge([{ id, ...payload } as TimeEntry]);
            return id;
        },

        async updateEntry(entryId: string, patch: Partial<TimeEntry>): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            this.saving = true;
            this.error = null;
            try {
                const updatedAt = new Date();
                const { id: _ignoredId, tenantId: _ignoredTenantId, ...editablePatch } = patch;
                const localPatch = { ...editablePatch, updatedAt };
                const payload = stripUndefined(localPatch) as Record<string, unknown>;
                const optionalFields: Array<keyof TimeEntry> = [
                    'taskId',
                    'taskTitle',
                    'projectId',
                    'projectName',
                    'clientId',
                    'clientName',
                    'description',
                ];
                for (const field of optionalFields) {
                    if (Object.prototype.hasOwnProperty.call(patch, field) && patch[field] === undefined) {
                        payload[field] = deleteField();
                    }
                }
                await updateTenantDocument($db, tenantId, 'timeEntries', entryId, payload as TenantDocumentPayload);
                this.entries = this._sort(this.entries.map(entry => entry.id === entryId ? { ...entry, ...localPatch } as TimeEntry : entry));
            } catch (error: any) {
                this.error = error?.message || 'Unable to update the time entry.';
                throw error;
            } finally {
                this.saving = false;
            }
        },

        async deleteEntry(entryId: string): Promise<void> {
            const { $db } = useNuxtApp();
            const tenantId = this._tenantId();
            this.saving = true;
            this.error = null;
            try {
                await deleteTenantDocument($db, tenantId, 'timeEntries', entryId);
                this.entries = this.entries.filter(entry => entry.id !== entryId);
            } catch (error: any) {
                this.error = error?.message || 'Unable to delete the time entry.';
                throw error;
            } finally {
                this.saving = false;
            }
        },
    },
});
